import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Check, Search } from 'lucide-react';
import { useState } from 'react';
import { PersonAvatar } from '@/shared/components/PersonAvatar';

export type FilterOption = { value: string; count: number };

type Props = {
  options: FilterOption[];
  selected: string[];
  multiple: boolean;
  people: boolean;
  searchLabel: string;
  onToggle: (value: string) => void;
  onClear: () => void;
};

/** The searchable list inside a filter menu: checkboxes when several can be picked, a tick when only one. */
export function FilterOptions({ options, selected, multiple, people, searchLabel, onToggle, onClear }: Props) {
  const [search, setSearch] = useState('');
  const shown = options.filter((o) => o.value.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <Stack sx={{ width: 260 }}>
      <Box sx={{ p: 1 }}>
        <TextField
          fullWidth
          autoComplete="off"
          placeholder={searchLabel}
          value={search}
          onChange={(e) => { setSearch(e.target.value); }}
          slotProps={{ htmlInput: { 'aria-label': searchLabel }, input: { startAdornment: <InputAdornment position="start"><Search size={14} aria-hidden /></InputAdornment> } }}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.8125rem', minHeight: 34 } }}
        />
      </Box>
      <Stack component="ul" role="listbox" aria-multiselectable={multiple} sx={{ listStyle: 'none', m: 0, px: 0.75, pb: 0.75, maxHeight: 280, overflowY: 'auto' }}>
        {shown.length === 0 ? <Typography variant="caption" sx={{ px: 1, py: 1, color: 'text.secondary' }}>No match</Typography> : null}
        {shown.map((o) => {
          const isOn = selected.includes(o.value);
          return (
            <li key={o.value}>
              <ButtonBase role="option" aria-selected={isOn} onClick={() => { onToggle(o.value); }} sx={rowSx}>
                {multiple ? <Checkbox size="small" checked={isOn} tabIndex={-1} disableRipple sx={{ p: 0 }} slotProps={{ input: { 'aria-hidden': true } }} /> : null}
                {people ? <PersonAvatar name={o.value} size={20} /> : null}
                <Typography variant="body2" sx={{ flexGrow: 1, textAlign: 'left', fontSize: '0.8125rem' }}>{o.value}</Typography>
                {!multiple && isOn ? <Check size={14} aria-hidden /> : null}
              </ButtonBase>
            </li>
          );
        })}
      </Stack>
      {multiple ? (
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{selected.length} selected</Typography>
          <ButtonBase onClick={onClear} disabled={selected.length === 0} sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'primary.main', borderRadius: 0.5, px: 0.5, '&.Mui-disabled': { color: 'text.disabled' } }}>
            Clear
          </ButtonBase>
        </Stack>
      ) : null}
    </Stack>
  );
}

const rowSx = {
  width: '100%',
  gap: 1.25,
  minHeight: 36,
  px: 1,
  borderRadius: 1,
  transition: 'background-color 120ms',
  '&:hover': { bgcolor: 'surface.subtle' },
} as const;
